import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const PriceChart = ({ tokenMint, height = 200, showControls = true }) => {
  const { tokens } = useAuth();
  const [priceHistory, setPriceHistory] = useState([]);
  const [timeframe, setTimeframe] = useState('4h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

  useEffect(() => {
    if (tokenMint && tokens?.access_token) {
      fetchPriceHistory();
    }
  }, [tokenMint, timeframe, tokens?.access_token]);

  useEffect(() => {
    if (priceHistory.length > 0 && canvasRef.current) {
      drawChart();
    }
  }, [priceHistory]);

  const fetchPriceHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${API_BASE_URL}/prices/${tokenMint}/history?timeframe=${timeframe}&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPriceHistory(data.data.history || []);
      } else {
        throw new Error('Failed to fetch price history');
      }
    } catch (error) {
      console.error('Error fetching price history:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const drawChart = () => {
    const canvas = canvasRef.current;
    if (!canvas || priceHistory.length === 0) return;

    const ctx = canvas.getContext('2d');
    const { width, height: canvasHeight } = canvas;

    // Clear canvas
    ctx.clearRect(0, 0, width, canvasHeight);

    // Get price range
    const prices = priceHistory.map(p => p.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;

    if (priceRange === 0) return;

    // Chart dimensions
    const padding = 40;
    const chartWidth = width - 2 * padding;
    const chartHeight = canvasHeight - 2 * padding;

    // Draw background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, width, canvasHeight);

    // Draw grid lines
    ctx.strokeStyle = '#e9ecef';
    ctx.lineWidth = 1;
    
    // Horizontal grid lines
    for (let i = 0; i <= 5; i++) {
      const y = padding + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
    }

    // Vertical grid lines
    const timePoints = Math.min(priceHistory.length, 10);
    for (let i = 0; i <= timePoints; i++) {
      const x = padding + (chartWidth / timePoints) * i;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, canvasHeight - padding);
      ctx.stroke();
    }

    // Draw price line
    ctx.strokeStyle = '#007bff';
    ctx.lineWidth = 2;
    ctx.beginPath();

    priceHistory.forEach((point, index) => {
      const x = padding + (chartWidth / (priceHistory.length - 1)) * index;
      const y = padding + chartHeight - ((point.price - minPrice) / priceRange) * chartHeight;

      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Draw price points
    ctx.fillStyle = '#007bff';
    priceHistory.forEach((point, index) => {
      const x = padding + (chartWidth / (priceHistory.length - 1)) * index;
      const y = padding + chartHeight - ((point.price - minPrice) / priceRange) * chartHeight;
      
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, 2 * Math.PI);
      ctx.fill();
    });

    // Draw price labels
    ctx.fillStyle = '#6c757d';
    ctx.font = '12px Arial';
    ctx.textAlign = 'right';
    
    for (let i = 0; i <= 5; i++) {
      const price = minPrice + (priceRange / 5) * (5 - i);
      const y = padding + (chartHeight / 5) * i;
      ctx.fillText(price.toFixed(6), padding - 5, y + 4);
    }

    // Draw time labels
    ctx.textAlign = 'center';
    const labelInterval = Math.max(1, Math.floor(priceHistory.length / 5));
    
    priceHistory.forEach((point, index) => {
      if (index % labelInterval === 0) {
        const x = padding + (chartWidth / (priceHistory.length - 1)) * index;
        const time = new Date(point.timestamp).toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
        ctx.fillText(time, x, canvasHeight - 5);
      }
    });

    // Draw current price indicator
    if (priceHistory.length > 0) {
      const currentPrice = priceHistory[priceHistory.length - 1].price;
      const y = padding + chartHeight - ((currentPrice - minPrice) / priceRange) * chartHeight;
      
      // Draw horizontal line
      ctx.strokeStyle = '#28a745';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw price label
      ctx.fillStyle = '#28a745';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`Current: ${currentPrice.toFixed(6)}`, width - padding + 5, y + 4);
    }
  };

  const formatPrice = (price) => {
    if (price < 0.000001) return price.toExponential(2);
    if (price < 0.01) return price.toFixed(6);
    return price.toFixed(4);
  };

  if (loading) {
    return (
      <div className="price-chart loading">
        <div className="loading-spinner"></div>
        <p>Loading price chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="price-chart error">
        <p>Failed to load price chart: {error}</p>
        <button onClick={fetchPriceHistory} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (priceHistory.length === 0) {
    return (
      <div className="price-chart no-data">
        <p>No price data available</p>
      </div>
    );
  }

  return (
    <div className="price-chart">
      {showControls && (
        <div className="chart-controls">
          <div className="timeframe-selector">
            {['1h', '4h', '24h', '7d'].map(tf => (
              <button
                key={tf}
                className={`timeframe-button ${timeframe === tf ? 'active' : ''}`}
                onClick={() => setTimeframe(tf)}
              >
                {tf}
              </button>
            ))}
          </div>
          
          <div className="chart-info">
            <span className="price-range">
              Range: {formatPrice(Math.min(...priceHistory.map(p => p.price)))} - {formatPrice(Math.max(...priceHistory.map(p => p.price)))}
            </span>
          </div>
        </div>
      )}
      
      <div className="chart-container">
        <canvas
          ref={canvasRef}
          width={600}
          height={height}
          style={{ width: '100%', height: `${height}px` }}
        />
      </div>
      
      <div className="chart-stats">
        <div className="stat">
          <span className="stat-label">Data Points:</span>
          <span className="stat-value">{priceHistory.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Last Update:</span>
          <span className="stat-value">
            {priceHistory.length > 0 && new Date(priceHistory[priceHistory.length - 1].timestamp).toLocaleTimeString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PriceChart;